import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from '../../../credit-control/src/providers/AuthProvider';
import type {
  TenantBasicInfoApi,
  UserContextApi,
} from '../../../credit-control/src/types/api/user-context';

export interface UserContextValue {
  userContext: UserContextApi | null;
  tenants: TenantBasicInfoApi[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const UserContextContext = createContext<UserContextValue | null>(null);

export function useUserContext(): UserContextValue {
  const context = useContext(UserContextContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
}

interface UserContextProviderProps {
  apiBaseUrl: string;
  children: ReactNode;
}

export function UserContextProvider({
  apiBaseUrl,
  children,
}: UserContextProviderProps): React.ReactElement {
  const { getAccessToken, instanceId } = useAuth();
  const [userContext, setUserContext] = useState<UserContextApi | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserContext = async () => {
    if (!getAccessToken) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      if (instanceId) {
        headers['X-Instance-Id'] = instanceId;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/usercontext`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user context: ${response.status}`);
      }

      const data: UserContextApi = await response.json();
      setUserContext(data);
    } catch (err) {
      console.error('Error fetching user context:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserContext();
  }, [getAccessToken, instanceId, apiBaseUrl]);

  const value: UserContextValue = {
    userContext,
    tenants: userContext?.tenants ?? [],
    isLoading,
    error,
    refetch: fetchUserContext,
  };

  return (
    <UserContextContext.Provider value={value}>
      {children}
    </UserContextContext.Provider>
  );
}
